import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { DEFAULT_ADS_SEARCH } from "../lib/urlState.js";
import { rum } from "../perf/rum.js";
import { useUiStore } from "../store/uiStore.js";

/**
 * ⌘K 命令面板（键盘优先的核心入口）。
 *
 * 设计取舍：
 *   - 手写实现而不是引入 cmdk：面板需要与「路由 + 主题 + 密度 + 基准脚本」这些内部动作耦合，
 *     自己实现能保证零额外运行时依赖、且键盘行为完全可控（cmdk 仍在依赖清单里，
 *     若后续需要分组/异步项/虚拟列表可以直接替换，接口就是下面的 Command 结构）。
 *   - 键盘：↑↓ 移动、Enter 执行、Esc 关闭、输入即筛选（子序列模糊匹配）。
 *   - 无障碍：listbox + option + aria-activedescendant，输入框保持焦点。
 */

interface Command {
  readonly id: string;
  readonly group: string;
  readonly label: string;
  readonly hint: string;
  readonly keywords: string;
  readonly run: () => void;
}

function fuzzyScore(query: string, text: string): number {
  if (query.length === 0) return 1;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  const direct = t.indexOf(q);
  if (direct >= 0) return 1000 - direct;
  let cursor = 0;
  let score = 0;
  for (const char of q) {
    const found = t.indexOf(char, cursor);
    if (found < 0) return -1;
    score += 1;
    cursor = found + 1;
  }
  return score;
}

export function CommandPalette() {
  const navigate = useNavigate();
  const open = useUiStore((state) => state.commandOpen);
  const setCommandOpen = useUiStore((state) => state.setCommandOpen);
  const setHelpOpen = useUiStore((state) => state.setHelpOpen);
  const setSettingsOpen = useUiStore((state) => state.setSettingsOpen);
  const pushToast = useUiStore((state) => state.pushToast);

  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const commands = useMemo<readonly Command[]>(() => {
    const go = (to: "/" | "/overview" | "/sync"): (() => void) => {
      return () => {
        rum.markNavStart();
        setCommandOpen(false);
        if (to === "/") void navigate({ to, search: DEFAULT_ADS_SEARCH });
        else void navigate({ to });
      };
    };

    return [
      {
        id: "nav-ads",
        group: "导航",
        label: "广告指标表（真实实体 · 游标分页）",
        hint: "G 然后 2",
        keywords: "ads table 广告 表格 实体 暂停 预算 虚拟化",
        run: go("/"),
      },
      {
        id: "nav-overview",
        group: "导航",
        label: "经营概览（MER / 真实收入）",
        hint: "G 然后 1",
        keywords: "overview mer pacing 概览 经营",
        run: go("/overview"),
      },
      {
        id: "nav-sync",
        group: "导航",
        label: "数据同步（水位 / 行数 / 失败原因）",
        hint: "G 然后 3",
        keywords: "sync status watermark 同步 水位 失败 shopify meta",
        run: go("/sync"),
      },
      {
        id: "nav-settings",
        group: "导航",
        label: "设置",
        hint: "大弹窗 · 关闭后仍在当前页",
        keywords: "settings 设置 品牌 店铺 账号 数据源",
        // 用户第三轮第 4 条：设置是**弹窗**，不是页面 —— 命令面板入口同样不导航，
        // 否则「关闭后停留在当前工作页面」这条从命令面板进来就不成立了。
        run: () => {
          setCommandOpen(false);
          setSettingsOpen(true);
        },
      },
      {
        id: "help",
        group: "帮助",
        label: "键盘快捷键一览",
        hint: "Shift + ?",
        keywords: "keyboard shortcuts 快捷键 帮助",
        run: () => {
          setCommandOpen(false);
          setHelpOpen(true);
        },
      },
    ];
  }, [navigate, setCommandOpen, setHelpOpen, setSettingsOpen, pushToast]);

  const results = useMemo(() => {
    const scored = commands
      .map((command) => ({
        command,
        score: fuzzyScore(query.trim(), `${command.label} ${command.keywords} ${command.group}`),
      }))
      .filter((entry) => entry.score >= 0);
    scored.sort((a, b) => b.score - a.score);
    return scored.map((entry) => entry.command);
  }, [commands, query]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  if (!open) return null;

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      setCommandOpen(false);
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) => Math.min(results.length - 1, index + 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(0, index - 1));
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      results[activeIndex]?.run();
    }
  };

  return (
    <div className="fixed inset-0 z-[var(--z-command)] flex items-start justify-center pt-[12vh]" onKeyDown={onKeyDown}>
      <div
        className="absolute inset-0 animate-overlay-in bg-overlay"
        aria-hidden="true"
        onClick={() => setCommandOpen(false)}
      />
      <div className="relative z-[var(--z-command)] w-full max-w-xl animate-panel-in overflow-hidden rounded-xl border border-line bg-raised elev-xl">
        <div className="border-b border-line-subtle px-4 py-3">
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="跳转到页面…"
            aria-label="命令面板搜索"
            aria-controls="command-list"
            aria-activedescendant={results[activeIndex] === undefined ? undefined : `command-${results[activeIndex].id}`}
            role="combobox"
            aria-expanded="true"
            className="w-full bg-transparent text-base text-ink outline-none placeholder:text-ink-3"
          />
        </div>
        <ul id="command-list" role="listbox" aria-label="命令列表" className="max-h-[52vh] overflow-auto py-1">
          {results.length === 0 ? (
            <li className="px-4 py-3 text-table text-ink-3">没有匹配的命令</li>
          ) : (
            results.map((command, index) => (
              <li key={command.id} role="option" aria-selected={index === activeIndex} id={`command-${command.id}`}>
                <button
                  type="button"
                  onMouseEnter={() => setActiveIndex(index)}
                  onClick={() => command.run()}
                  className={`flex w-full items-center justify-between gap-3 px-4 py-2 text-left transition-colors dur-fast ${
                    index === activeIndex ? "bg-selected" : "hover:bg-hover"
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-table text-ink">{command.label}</span>
                    <span className="block truncate text-meta text-ink-3">{command.hint}</span>
                  </span>
                  <span className="shrink-0 text-2xs uppercase tracking-caps text-ink-3">{command.group}</span>
                </button>
              </li>
            ))
          )}
        </ul>
        <div className="flex items-center justify-between border-t border-line-subtle bg-sunken px-4 py-2 text-2xs text-ink-3">
          <span>↑↓ 选择 · Enter 执行 · Esc 关闭</span>
          <span>⌘K / Ctrl+K 随时呼出</span>
        </div>
      </div>
    </div>
  );
}
